import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Container, Grid, Typography } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { Modal } from '@/components/_common';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { FileManagerInventoryFile } from '@/redux/slices/slice_types';
import { selectAllFileSources, selectFolderIsLoading } from '@/redux/selectors/fileManagerSelectors';
import { getFileSources, getFolder, resetFileManager } from '@/redux/slices/fileManagerSlice';
import BackForwardNav from '@/components/FileManager/FilesContainer/BackForwardNav';
import Path from '@/components/FileManager/FilesContainer/Path/Path';
import FileNavigationContainer from '@/components/FileManager/FilesContainer/FileNavigation/FileNavigationContainer';
import FolderContent from '@/components/FileManager/FilesContainer/FolderContent/FolderContent';
import FileManagerUpload from '@/components/FileManager/FileManagerUpload';

interface FileManagerModalProps {
  open: boolean;
  selectedFiles: FileManagerInventoryFile[];
  modalTitle: string;
  canUpload?: boolean;
  canCreateFolder?: boolean;
  blurb?: React.ReactNode;
  footer?: React.ReactNode;
  onClose(): void;
  handleFileSelection(item: FileManagerInventoryFile): void;
}

const FileManagerModal = ({
  open,
  selectedFiles,
  modalTitle,
  canUpload = false,
  canCreateFolder,
  blurb,
  footer,
  onClose,
  handleFileSelection
}: FileManagerModalProps) => {
  const dispatch = useAppDispatch();
  const fileLoading = useAppSelector(selectFolderIsLoading);
  const sources = useAppSelector(selectAllFileSources);
  const fileGridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fileLoading && fileGridRef.current) {
      fileGridRef.current.scrollLeft = 9999;
    }
  }, [fileLoading]);

  useEffect(() => {
    if (open) {
      dispatch(getFileSources());
    }

    return () => {
      if (open) {
        dispatch(resetFileManager());
      }
    };
  }, [open]);

  useEffect(() => {
    if (open && sources.length) {
      // Load the root folder of the first source when modal is opened
      dispatch(getFolder({ path: [sources[0].slug] }));
    }
  }, [sources?.[0]?.slug, open]);

  return (
    <div>
      <StyledModal open={open} onClose={onClose} title={modalTitle} icon={<PolarisIcon icon="cloud_link" height={26} style={{ opacity: 0.6 }} />}>
        <Grid container direction="row" justifyContent={'space-between'}>
          <Grid item>
            <Container sx={{ whiteSpace: 'nowrap' }}>
              <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap">
                <Grid container item alignItems="center" wrap="nowrap">
                  <BackForwardNav />
                  <Grid item>
                    <Path homeBucket withHome />
                  </Grid>
                </Grid>
              </Grid>
            </Container>
          </Grid>
          {blurb && (
            <Grid item>
              <Typography sx={{ px: 1, py: 1, whiteSpace: 'nowrap', opacity: 0.5 }}>{blurb}</Typography>
            </Grid>
          )}
        </Grid>

        <Grid container direction="column" flex={1} flexWrap="nowrap">
          {/* FILE BROWSER */}
          <FileManagerWrapper ref={fileGridRef} container wrap="nowrap">
            <FileNavigationContainer />
            <FolderContent addAction={handleFileSelection} selectedFiles={selectedFiles} canCreateFolder={canCreateFolder} />
            {canUpload && (
              <div style={{ position: 'absolute', right: 40, bottom: 30, width: 300 }}>
                <FileManagerUpload />
              </div>
            )}
          </FileManagerWrapper>

          {footer}
        </Grid>
      </StyledModal>
    </div>
  );
};

export default FileManagerModal;

const StyledModal = styled(Modal)`
  .MuiPaper-root {
    max-width: 80vw;
    height: 80vh;
    display: flex;
    flex-direction: column;

    & > .MuiBox-root {
      display: flex;
      flex-direction: column;
      padding-top: ${({ theme }) => theme.spacing(2)};
      overflow: visible;
      flex: 1;
      max-height: unset;
    }
  }
`;

const FileManagerWrapper = styled(Grid)`
  display: flex;
  border: 1px solid ${({ theme }) => theme.palette.action.selected};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  transition: ease-in-out height 200ms;
  flex: 1 1 0;
  overflow-y: auto;

  .MuiList-root {
    background: ${({ theme }) => theme.palette.background.default};

    .MuiListItemButton-root.Mui-selected {
      background: ${({ theme }) => theme.palette.action.focus};
    }
  }

  & > nav {
    flex: 1 1 0;
    max-width: 400px;
    min-width: 17vw;
    height: unset;
    min-height: unset;
    overflow: auto;
    padding: 0;
    border-right: 1px solid ${(props) => props.theme.palette.action.disabledBackground};
    &:first-of-type {
      max-width: 20em;
      min-width: 20em;
    }
    &:last-of-type {
      min-width: 40vw;
      max-width: none;
      border: 0;
    }
  }
`;
